import { AGED_CARE_SCHEDULE } from '../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { formatCurrency } from '../../utils/index.ts';
import type { RadFundingBag } from './getRadFundingTooltipData.tsx';
import type { ProjectionYear } from './FinancialImpactPanel.tsx';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';

/** One row of a Suggestion Engine strategy's asset allocation (which assets it sells/keeps). */
export interface AssetAllocationRow {
    status?: 'partiallySold' | 'sold' | 'kept' | string;
    liquidatedAmount?: number | string;
    assetName?: string;
    type?: string;
    assetType?: string;
    [key: string]: unknown;
}

/** A Suggestion Engine evaluated strategy/scenario row — shape varies by generator, kept loose.
 * `strategy` is optionally self-nested: some callers pass a SavedStrategy-like wrapper whose real
 * strategy payload lives one level down (`selectedStrategy.strategy`). */
export interface EvaluatedStrategy {
    id?: string;
    name?: string;
    source?: string;
    projections?: ProjectionYear[];
    radFunding?: RadFundingBag;
    strategy?: EvaluatedStrategy;
    assetAllocation?: AssetAllocationRow[];
    projectedEstateValue?: number;
    strategyOptions?: {
        downsizeDetails?: { radAllocation?: number | string };
        [key: string]: unknown;
    };
    /** The means-test lever this strategy layers on, where it has one — annuity, gift or funeral bond. */
    meansTestStrategy?: {
        type?: string;
        amount?: number;
        purchaseAmount?: number;
        [key: string]: unknown;
    } | null;
    snapshot?: {
        legacyValue?: number | string;
        pension?: number | string;
        netCashflow?: number | string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export interface FutureEventAdjustedLegacyParams {
    strategy: EvaluatedStrategy | null | undefined;
    legacyYears?: number;
    partnerEntryYear?: number;
    partnerRoomPrice?: number;
    originalEntrantAliveAtEvent?: boolean;
    baseAgedCareCosts?: AgedCareCosts;
}

export interface FutureEventAdjustedLegacyResult {
    baselineLegacy: number;
    adjustedLegacy: number;
    adjustmentDelta: number;
    assumptions: {
        partnerEntryYear: number;
        partnerRoomPrice: number;
        radPct: number;
        originalEntrantAliveAtEvent: boolean;
        yearsAffected: number;
        additionalAnnualCareCost: number;
        additionalLifecycleCost: number;
        releasedRadFromInitialEntrant: number;
    };
}

export interface LowerRadNearBestResult {
    strategy: EvaluatedStrategy;
    maxLegacy: number;
    bandUsd: number;
    topLegacyStrategyName: string;
}

/**
 * Display name for a RAD strategy, showing the ACTUAL RAD funded (net of any CGT on assets sold to fund it)
 * — both the dollar figure and the percentage. The stored name's "65%" is the funding TARGET; once CGT is
 * netted the real RAD can be, e.g., $320,781 on a $500k room = 64%. We recompute the % from the funded
 * dollars (rounded to the nearest whole percent) and append the amount:
 *   "Pay 65% RAD, Keep Home"  ->  "Pay 64% RAD ($320,781), Keep Home".
 * No-CGT strategies are unchanged (net == target). Also strips the "(funding order)" suffix — that one
 * and no other; see FUNDING_ORDER_SUFFIX for why the difference matters.
 * Shared by the SE cards and the digital SOA so the two never disagree.
 */
/**
 * "Do Nothing" is the right name for a BASELINE and the wrong name for ADVICE.
 *
 * The scenario is the no-lump-sum case: the client still moves into care, and the accommodation is
 * still paid — every day of it — as a Daily Accommodation Payment instead of a refundable deposit.
 * That is a funding decision with real consequences (a DAP is not refunded to the estate), and calling
 * it "Do Nothing" tells a client the tool recommends inaction when the whole premise is that care must
 * be paid for somehow.
 *
 * Renamed at the DISPLAY layer only. The scenario keeps its internal name because a dozen places
 * identify the baseline by it (`name === 'do nothing'`), and renaming the identity would silently
 * detach the comparison every card is measured against.
 */
const BASELINE_STRATEGY_DISPLAY_NAME = 'Pay the DAP';

/**
 * The funding-order suffix, and ONLY that.
 *
 * This used to be "any trailing parenthesised group", which is what a funding order happens to look
 * like — and so does everything else a candidate name might carry. Two strategies that differed only
 * in a parenthesised suffix therefore displayed under one name: "+ Pay Down Debt (keep repayments)"
 * and "(lower repayments)" both read as "+ Pay Down Debt", and a $200k lifetime annuity with the
 * capital returned read the same as one with nil residual — two rows, one name, and a $200,000
 * difference in the estate between them (seen in the compare-all table, 2026-08-27). An adviser
 * reading that rightly concludes the tool is broken.
 *
 * A funding order is a list of asset-type labels joined by an arrow, or the literal "Default". That
 * is narrow enough to strip confidently and to leave everything else alone.
 */
export function stripFundingOrderSuffix(name: string = ''): string {
    return String(name).replace(FUNDING_ORDER_SUFFIX, '').trim();
}

const FUNDING_ORDER_SUFFIX = /\s*\((?:Default|(?:Bank|ABP|Shares|Term deposit)(?:\s*(?:->|→)\s*(?:Bank|ABP|Shares|Term deposit))*)\)\s*$/i;

export function formatRadStrategyDisplayName(
    name: string = '',
    radPaidNet: number | null = null,
    roomPrice: number | null = null,
    /** The second room, where both of a couple are in care: the name then says "of each room" — or each room's own share, where what was left could not match the first's. */
    secondRoom: { radPaid: number; roomPrice: number; name?: string; leadName?: string } | null = null,
    /** A change of home (8 Oct 2026): "Stay where you are", or "Move — …" with the refund named. */
    move: { stayPut: boolean; fromRefund: number } | null = null,
    /** Leaving a retirement village (8 Oct 2026): the part of the deposit the village money pays, named. */
    village: { fromVillage: number } | null = null,
): string {
    const clean = String(name).replace(FUNDING_ORDER_SUFFIX, '').trim();
    if (move?.stayPut || /^stay put\b/i.test(clean)) return 'Stay where you are';
    // Matched on the leading phrase so the suffixes candidates carry — "+ Clear Debt", "+ Pay Down
    // Debt (keep repayments)" — survive the rename and read as what they are: "Pay the DAP + Clear Debt".
    if (/^do nothing\b/i.test(clean)) {
        const baseline = clean.replace(/^do nothing\b/i, BASELINE_STRATEGY_DISPLAY_NAME);
        // Two rooms, nothing paid up front on either: say so, or "Pay the DAP" reads as one room.
        return secondRoom && secondRoom.roomPrice > 0 ? baseline.replace(BASELINE_STRATEGY_DISPLAY_NAME, `${BASELINE_STRATEGY_DISPLAY_NAME} on both rooms`) : baseline;
    }
    if (!(radPaidNet != null && radPaidNet > 0)) return clean;
    return clean.replace(/(\d+)(%\s+RAD\b)/i, (_match, pct, rest) => {
        const shownPct = roomPrice != null && roomPrice > 0 ? Math.round((radPaidNet / roomPrice) * 100) : pct;
        if (secondRoom && secondRoom.roomPrice > 0) {
            const secondPct = Math.round((secondRoom.radPaid / secondRoom.roomPrice) * 100);
            if (Math.abs(secondPct - Number(shownPct)) <= 1) {
                return `${shownPct}% of each room (${formatCurrency(radPaidNet)} + ${formatCurrency(secondRoom.radPaid)})`;
            }
            const first = secondRoom.leadName ? `${secondRoom.leadName}'s room` : 'the first room';
            const second = secondRoom.name ? `${secondRoom.name}'s` : 'the second';
            return `${shownPct}% of ${first} and ${secondPct}% of ${second} (${formatCurrency(radPaidNet)} + ${formatCurrency(secondRoom.radPaid)})`;
        }
        // "all from …" where that money pays the whole deposit, rather than the same figure twice
        // (owner, 9 Oct 2026: "($750,000, $750,000 of it from the village money)" read oddly).
        const fromSource = (amount: number, source: string) =>
            Math.abs(amount - radPaidNet) < 1 ? `, all from the ${source}` : `, ${formatCurrency(Math.round(amount))} from the ${source}`;
        const fromRefund = move && move.fromRefund > 0.5 ? fromSource(move.fromRefund, 'refund') : '';
        const fromVillage = village && village.fromVillage > 0.5 ? fromSource(village.fromVillage, 'village money') : '';
        return `${shownPct}${rest} (${formatCurrency(radPaidNet)}${fromRefund}${fromVillage})`;
    }).replace(/^Pay\b/, move ? 'Move — pay' : 'Pay');
}

export function extractRadPercentageFromStrategyName(strategyName: string = ''): number | null {
    const match = String(strategyName).match(/Pay\s+(\d+)%\s+RAD/i);
    if (!match) return null;
    const pct = parseInt(match[1], 10);
    return Number.isFinite(pct) ? pct : null;
}

/**
 * The most a provider keeps of a RAD: 2% a year for the first five years.
 *
 * Named rather than inlined because it is the number that makes a deposit different in kind from an
 * annuity — the deposit comes back, this share excepted.
 */
export const RAD_MAX_RETENTION_SHARE = 0.10;

export function estimateRadPaidForStrategy(strategy: EvaluatedStrategy | null | undefined): number {
    if (!strategy) return 0;
    const fundedFromLiquidAssets = Object.values(strategy.radFunding || {}).flatMap((liquidations) => {
        if (Array.isArray(liquidations)) return liquidations;
        return liquidations ? [liquidations] : [];
    }).reduce((sum, item) => sum + (parseFloat(String(item?.amount)) || 0), 0);

    const fundedFromDownsize = parseFloat(String(strategy?.strategyOptions?.downsizeDetails?.radAllocation)) || 0;
    // Money the engine put to the room that was never in the funding bag: a village's exit payment
    // (8 Oct 2026) and the refund of a current deposit on a change of home. Without these, two
    // "Pay 100% RAD" rows funded mostly by the village money read as $50,000 deposits (owner-caught,
    // 9 Oct 2026: "$50,000 vs $50,000 … paying $0 less").
    const row = strategy as unknown as { villageExit?: { fromVillage?: number } | null; facilityMove?: { fromRefund?: number } | null };
    const fundedFromVillage = parseFloat(String(row?.villageExit?.fromVillage)) || 0;
    const fundedFromRefund = parseFloat(String(row?.facilityMove?.fromRefund)) || 0;
    return fundedFromLiquidAssets + fundedFromDownsize + fundedFromVillage + fundedFromRefund;
}

/**
 * Money the client CANNOT GET BACK if circumstances change.
 *
 * OWNER-CAUGHT, 2026-09-01: "the DAP + annuity comes in as lower RAD, near-best legacy, but this is
 * a bit contradictory. The intent of the second option is when you are locking away less money for
 * almost the same estate benefit. With this strategy we might not be locking money away into the
 * RAD, but we are locking it away into an annuity — and the annuity purchase price is more than the
 * optimal legacy strategy which was only $440k."
 *
 * Exactly right, and the card was ranking on the wrong number. It picked the strategy with the
 * lowest RAD and sold it as "worth considering if keeping flexibility and liquidity matters" — of a
 * plan that put $500,000 into a lifetime annuity to avoid a $440,000 deposit. That is the reverse of
 * what it claimed:
 *
 *   A RAD IS REFUNDED when care ends (less a 2% a year retention for the first five years). It is
 *   the most recoverable thing on this list.
 *   AN ANNUITY IS NEVER REFUNDED. The capital is committed for life, whatever the residual capital
 *   value returns to the estate at the end.
 *   A GIFT IS GONE, and a FUNERAL BOND is locked until death.
 *
 * So "less locked away" has to count the levers, and a RAD has to count for less than an annuity of
 * the same size. Ranking on RAD alone made the card recommend the least liquid plan in the field on
 * the grounds of liquidity.
 */
export function estimateLockedCapitalForStrategy(strategy: EvaluatedStrategy | null | undefined): number {
    if (!strategy) return 0;
    const lever = strategy.meansTestStrategy || {};
    const irrevocable =
        (parseFloat(String(lever.purchaseAmount)) || 0)   // annuity — never refundable
        + (parseFloat(String(lever.amount)) || 0);        // gift or funeral bond — gone, or locked until death

    /*
     * The RAD counts at its RETENTION, not its face value, because that is the part the client does
     * not get back. Five years at 2% is the maximum the provider keeps, so it is the honest measure
     * of what a deposit actually costs in recoverable money — and it stops a refundable deposit
     * being ranked as though it were as final as an annuity.
     */
    const radRetention = estimateRadPaidForStrategy(strategy) * RAD_MAX_RETENTION_SHARE;
    return irrevocable + radRetention;
}

export function computeFutureEventAdjustedLegacy({
    strategy,
    legacyYears = 5,
    partnerEntryYear = 3,
    partnerRoomPrice = 0,
    originalEntrantAliveAtEvent = true,
    baseAgedCareCosts = {},
}: FutureEventAdjustedLegacyParams): FutureEventAdjustedLegacyResult | null {
    if (!strategy || !partnerRoomPrice || partnerEntryYear > legacyYears) return null;

    const yearsAffected = Math.max(0, legacyYears - partnerEntryYear + 1);
    if (yearsAffected === 0) return null;

    // First-pass heuristic: infer RAD intent from strategy name so the partner-later
    // overlay stays aligned with baseline strategy behavior without re-running projections.
    const radPct = extractRadPercentageFromStrategyName(strategy.name) ?? 50;
    const targetPartnerRad = partnerRoomPrice * (radPct / 100);
    const targetPartnerDapBase = Math.max(0, partnerRoomPrice - targetPartnerRad);

    const thisStrategyRadPaid = estimateRadPaidForStrategy(strategy);
    const yearsSinceInitialAdmission = Math.max(1, partnerEntryYear - 1);
    const retentionYears = Math.min(yearsSinceInitialAdmission, 5);
    const retainedFromInitialRad = thisStrategyRadPaid * (1 - 0.02 * retentionYears);
    const releasedRadFromInitialEntrant = originalEntrantAliveAtEvent ? 0 : Math.max(0, retainedFromInitialRad);

    // If the original entrant has passed before the partner enters, released RAD can be
    // reused and lowers remaining DAP exposure for the second room.
    const partnerDapBaseAfterReuse = Math.max(0, targetPartnerDapBase - releasedRadFromInitialEntrant);

    const basicDailyFee = baseAgedCareCosts?.basicDailyFee || 70;
    const dailyHsc = baseAgedCareCosts?.dailyHSC || 0;
    const dailyNccc = baseAgedCareCosts?.dailyNCCC || 0;
    const dailyExtraServiceFee = baseAgedCareCosts?.dailyExtraServiceFee || 0;
    const dailyTotalFees = basicDailyFee + dailyHsc + dailyNccc + dailyExtraServiceFee;
    const annualDap = partnerDapBaseAfterReuse * AGED_CARE_SCHEDULE.MPIR;
    const additionalAnnualCareCost = annualDap + (dailyTotalFees * 365);
    // Intentionally simple v1 overlay: apply the same annualized cost for each remaining
    // year in the horizon. This is a bounded stress lens, not a full second-life branch.
    const additionalLifecycleCost = additionalAnnualCareCost * yearsAffected;

    const currentLegacy = parseFloat(String(strategy?.snapshot?.legacyValue)) || 0;
    const adjustedLegacy = currentLegacy - additionalLifecycleCost;
    const adjustmentDelta = adjustedLegacy - currentLegacy;

    return {
        baselineLegacy: currentLegacy,
        adjustedLegacy,
        adjustmentDelta,
        assumptions: {
            partnerEntryYear,
            partnerRoomPrice,
            radPct,
            originalEntrantAliveAtEvent,
            yearsAffected,
            additionalAnnualCareCost,
            additionalLifecycleCost,
            releasedRadFromInitialEntrant,
        },
    };
}

/**
 * Option A: among strategies within `bandUsd` of the best baseline projected estate,
 * pick the one with the lowest estimated RAD paid. Returns null if the winner is
 * already the top-legacy strategy (no separate card needed).
 */
export function pickLowerRadNearBestStrategy(strategies: EvaluatedStrategy[] | null | undefined, bandUsd: number = 5000): LowerRadNearBestResult | null {
    if (!Array.isArray(strategies) || strategies.length === 0) return null;

    let maxLegacy = -Infinity;
    let topStrategy: EvaluatedStrategy | null = null;
    for (const s of strategies) {
        const L = parseFloat(String(s?.snapshot?.legacyValue)) || 0;
        if (L > maxLegacy) {
            maxLegacy = L;
            topStrategy = s;
        }
    }
    if (!topStrategy || maxLegacy <= 0) return null;

    const threshold = maxLegacy - bandUsd;
    // Only a plan that genuinely pays a SMALLER deposit is a lower-RAD alternative (owner-caught,
    // 9 Oct 2026: "Pay 100% RAD" was offered beside "Pay 100% RAD + $16k funeral bond" as "$750,000 vs
    // $750,000 … paying $0 less" — it tied up less only because it had no bond).
    const topRad = estimateRadPaidForStrategy(topStrategy);
    const candidates = strategies.filter((s) => (parseFloat(String(s?.snapshot?.legacyValue)) || 0) >= threshold && estimateRadPaidForStrategy(s) < topRad - 1);
    if (candidates.length === 0) return null;

    /*
     * Ranked on LOCKED CAPITAL, not RAD paid — see estimateLockedCapitalForStrategy. The card exists
     * to offer a plan that ties up less of the client's money for near-identical estate; ranking on
     * the deposit alone let a $500,000 annuity win the slot for "avoiding" a $440,000 refundable one.
     */
    let best: EvaluatedStrategy | null = null;
    let bestLocked = Infinity;
    let bestLegacy = -Infinity;
    for (const s of candidates) {
        const locked = estimateLockedCapitalForStrategy(s);
        const leg = parseFloat(String(s?.snapshot?.legacyValue)) || 0;
        if (locked < bestLocked || (locked === bestLocked && leg > bestLegacy)) {
            bestLocked = locked;
            bestLegacy = leg;
            best = s;
        }
    }

    if (!best || !topStrategy?.name || best.name === topStrategy.name) return null;
    /*
     * And it must genuinely tie up less than the strategy it is offered as an alternative to. Without
     * this the card would still appear when the "alternative" commits MORE of the client's money —
     * which is not an alternative, it is a worse plan with a friendlier label.
     */
    if (estimateLockedCapitalForStrategy(best) >= estimateLockedCapitalForStrategy(topStrategy)) return null;

    return {
        strategy: best,
        maxLegacy,
        bandUsd,
        topLegacyStrategyName: topStrategy.name,
    };
}

